import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  AuthorizationTokenWithKeyPair,
  OidcTokenFactory,
  ExchangeTokenFactory,
  SystemConfigs,
  getJwtPayload,
  ServiceError,
} from "@ncino/pa-authentication-client-webclient";
import type { OidcTokenFactoryConfig } from "@ncino/pa-authentication-client-webclient";
import { AuthClient } from "@ncino/pa-authorization-client-webclient";
import { v4 as uuid } from "uuid";
import { writeActivePortfolioId } from "@/shared/hooks/useActivePortfolio";

export interface AuthState {
  isAuthenticated: boolean;
  tokenProvider: () => Promise<AuthorizationTokenWithKeyPair>;
  authClient: AuthClient;
  principalId: string;
}

export const AUTH_REDIRECT_CODES = new Set([401, 403, 405]);
const BUFFER_MS = 30_000;

type FailureHandler = (event: {
  reAuthRedirectUrl: string;
  responseCode: number;
  remainingRetries: number;
}) => Promise<string>;

function createAutoTokenFactoryManager(
  code: string,
  appRedirectUrl: string,
  oidcFailureHandler: FailureHandler,
  exchangeFailureHandler: FailureHandler
) {
  const oidcConfig: OidcTokenFactoryConfig = { clientId: "pax", appRedirectUrl };
  return OidcTokenFactory.fetchOidcConfig(oidcConfig)
    .then(() => OidcTokenFactory.oidcTokenFactoryManager(code, oidcFailureHandler, oidcConfig))
    .catch(() =>
      ExchangeTokenFactory.exchangeTokenFactoryManager(code, exchangeFailureHandler, {
        appRedirectUrl,
      })
    );
}

let exchangeLambda: (() => Promise<AuthorizationTokenWithKeyPair>) | null = null;
let factoryManagerPromise: Promise<{
  factory: () => Promise<AuthorizationTokenWithKeyPair>;
}> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

// Whether this boot carried a PA→PAX `?portfolioId=` handoff. initAuth strips the param
// from the URL before the router runs, so it records the fact here for beforeLoad to read
// when reconciling the active-portfolio override against the authenticated principal.
let bootCarriedHandoff = false;
export function bootHadPortfolioHandoff(): boolean {
  return bootCarriedHandoff;
}

// Proactively refreshes the token 30s before expiry so the cached value is always
// warm. exchangeTokenFactory would refresh lazily on the next call anyway, but this
// eliminates the latency spike on the first request after expiry.
function scheduleRefresh(token: AuthorizationTokenWithKeyPair): void {
  clearTimeout(refreshTimer);
  const exp = getJwtPayload(token.token ?? "").exp ?? 0;
  const delay = exp * 1000 - BUFFER_MS - Date.now();
  if (delay > 0) {
    refreshTimer = setTimeout(async () => {
      const refreshed = await exchangeLambda!();
      scheduleRefresh(refreshed);
    }, delay);
  }
}

export function initAuth(): void {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");

  // A `?portfolioId=` param signals a fresh PA→PAX handoff: PA's current portfolio
  // takes precedence over any in-session pick. Seed the override before React mounts so
  // the first render already reflects PA's portfolio.
  const handoffPortfolioId = params.get("portfolioId");
  let capturedHandoff = false;
  if (handoffPortfolioId !== null) {
    const parsed = Number(handoffPortfolioId);
    if (Number.isFinite(parsed)) {
      writeActivePortfolioId(parsed);
      capturedHandoff = true;
      bootCarriedHandoff = true;
    }
  }

  const oidcFailureHandler: FailureHandler = async ({ reAuthRedirectUrl, responseCode }) => {
    if (AUTH_REDIRECT_CODES.has(responseCode)) {
      window.location.href = reAuthRedirectUrl;
      throw new ServiceError(`AUTH_REQUIRED`, responseCode);
    }
    return "";
  };

  const exchangeFailureHandler: FailureHandler = async ({ responseCode }) => {
    if (AUTH_REDIRECT_CODES.has(responseCode)) {
      redirectToExchange();
      throw new ServiceError(`AUTH_REQUIRED`, responseCode);
    }
    return "";
  };

  if (code) {
    params.delete("code");
  }

  // Strip both transient handoff params from the URL so a later refresh (which carries
  // neither) is correctly treated as "keep the in-session pick", not a fresh handoff.
  if (code || capturedHandoff) {
    params.delete("portfolioId");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
    window.history.replaceState({}, "", newUrl);
  }

  factoryManagerPromise = createAutoTokenFactoryManager(
    code ?? "",
    window.location.origin + window.location.pathname + window.location.search,
    oidcFailureHandler,
    exchangeFailureHandler
  );
}

function redirectToExchange(): void {
  const exchangePageUrl = SystemConfigs.getExchangePageUrl();
  const appUrl = encodeURIComponent(window.location.href);
  window.location.href = `${exchangePageUrl}?redirect=${appUrl}`;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [principalId, setPrincipalId] = useState("");

  const tokenProvider = useCallback(async (): Promise<AuthorizationTokenWithKeyPair> => {
    if (!exchangeLambda) {
      exchangeLambda = (await factoryManagerPromise!).factory;
    }
    const token = await exchangeLambda();
    if (!refreshTimer) {
      scheduleRefresh(token);
    }

    const { sub } = getJwtPayload(token.token ?? "");
    setPrincipalId(sub ?? "");
    setIsAuthenticated(true);
    return token;
  }, []);

  const authClient = useMemo(
    () =>
      new AuthClient({
        bearerTokenProvider: tokenProvider,
      }),
    [tokenProvider]
  );

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated,
      tokenProvider,
      authClient,
      principalId,
    }),
    [isAuthenticated, tokenProvider, authClient, principalId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
